import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Button, Modal, Empty, Spin, Typography, Space, Tag, Popconfirm, message } from 'antd';
import { SearchOutlined, EyeOutlined, DownloadOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { signsAPI } from '../services/api';
import Header from '../components/Header';
import './SVGsPage.css';
import '../App.css';

const { Title, Text } = Typography;

export default function SVGsPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchProjects = useCallback(async (opts = {}) => {
    const { quiet = false } = opts;
    if (!quiet) setLoading(true);
    try {
      const res = await signsAPI.list(1, 1000); // Get all projects
      // Filter only projects that have SVG content
      const projectsWithSVG = (res.data.projects || []).filter(p => p.svg_content);
      setProjects(projectsWithSVG);
      setError('');
    } catch (err) {
      setError('Could not load SVGs');
      setProjects([]);
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleDownload = (e, project) => {
    if (e) {
      e.stopPropagation();
    }
    if (!project.svg_content) return;
    
    const blob = new Blob([project.svg_content], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${project.name || 'road_sign'}_${project.id}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleView = (e, project) => {
    if (e) {
      e.stopPropagation();
    }
    setSelectedProject(project);
    setPreviewOpen(true);
  };

  const handleOpenProject = (projectId) => {
    navigate(`/editor/${projectId}`);
  };

  const handleDeleteSvg = async (record) => {
    setDeletingId(record.id);
    try {
      await signsAPI.delete(record.id);
      message.success('SVG removed from library');
      if (selectedProject?.id === record.id) {
        setPreviewOpen(false);
        setSelectedProject(null);
      }
      await fetchProjects({ quiet: true });
    } catch (err) {
      message.error(err.response?.data?.error || err.message || 'Could not delete SVG');
    } finally {
      setDeletingId(null);
    }
  };

  const filteredProjects = projects.filter((p) =>
    `${p.name ?? ''}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
    `${p.sign_type ?? ''}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const [svgDataUrl, setSvgDataUrl] = useState(null);

  useEffect(() => {
    if (selectedProject?.svg_content) {
      // Process SVG to make it responsive
      let svg = selectedProject.svg_content;
      const vbMatch = svg.match(/<svg[^>]*\sviewBox="([^"]+)"/);
      const wMatch = svg.match(/<svg[^>]*\swidth="([^"]+)"/);
      const hMatch = svg.match(/<svg[^>]*\sheight="([^"]+)"/);
      
      // Add viewBox if missing
      if (!vbMatch && wMatch && hMatch) {
        try {
          const w = parseFloat(wMatch[1]);
          const h = parseFloat(hMatch[1]);
          if (!isNaN(w) && !isNaN(h) && w > 0 && h > 0) {
            svg = svg.replace(/<svg([^>]*)>/, `<svg$1 viewBox="0 0 ${w} ${h}">`);
          }
        } catch (e) {
          console.warn('Could not parse SVG dimensions:', e);
        }
      }
      
      // Make width/height responsive (use 100% so viewBox controls scaling)
      if (wMatch) {
        svg = svg.replace(/(<svg[^>]*)\swidth="[^"]+"/, '$1 width="100%"');
      }
      if (hMatch) {
        svg = svg.replace(/(<svg[^>]*)\sheight="[^"]+"/, '$1 height="100%"');
      }
      
      const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      setSvgDataUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setSvgDataUrl(null);
    }
  }, [selectedProject]);

  return (
    <div className="svgs-page">
      <Header
        onOpenRules={() => navigate('/rules')}
        onOpenLibrary={() => navigate('/svgs')}
        onOpenReference={() => navigate('/reference')}
        onNewEditor={() => navigate('/editor')}
        user={user}
        onLogout={onLogout}
      />

      <div className="svgs-container">
        <div className="svgs-header">
          <div className="svgs-title-section">
            <Title level={1} style={{ margin: 0, color: 'var(--text-primary)' }}>SVG Library</Title>
            <Text type="secondary" style={{ fontSize: 14 }}>View and download all your generated road sign SVGs</Text>
          </div>
        </div>

        <div className="svgs-toolbar">
          <Input
            placeholder="Search SVGs..."
            prefix={<SearchOutlined />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="large"
            style={{ maxWidth: 400 }}
          />
          <Text type="secondary" style={{ fontSize: 13 }}>
            {filteredProjects.length} {filteredProjects.length === 1 ? 'SVG' : 'SVGs'}
          </Text>
        </div>

        {error && (
          <div className="svgs-error">
            {error}
          </div>
        )}

        {loading ? (
          <div className="svgs-loading">
            <Spin size="large" />
            <Text type="secondary" style={{ marginTop: 16, display: 'block' }}>Loading SVGs...</Text>
          </div>
        ) : filteredProjects.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              searchQuery ? (
                <>
                  <Title level={4} style={{ color: 'var(--text-primary)' }}>No SVGs found</Title>
                  <Text type="secondary">Try adjusting your search query</Text>
                </>
              ) : (
                <>
                  <Title level={4} style={{ color: 'var(--text-primary)' }}>No SVGs yet</Title>
                  <Text type="secondary">Generate a sign in the editor (each run saves to the library) to see it here</Text>
                </>
              )
            }
          >
            {!searchQuery && (
              <Button type="primary" onClick={() => navigate('/editor')}>
                Open editor
              </Button>
            )}
          </Empty>
        ) : (
          <Table
            dataSource={filteredProjects.map(p => ({ ...p, key: p.id }))}
            columns={[
              {
                title: 'Preview',
                dataIndex: 'svg_content',
                key: 'preview',
                width: 120,
                render: (content, record) => (
                  content ? (
                    <div 
                      className="svg-preview-thumbnail"
                      onClick={() => handleView(null, record)}
                      style={{ cursor: 'pointer', width: 100, height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}
                      title="Click to view full size"
                    >
                      <div dangerouslySetInnerHTML={{ __html: content }} style={{ maxWidth: '100%', maxHeight: '100%' }} />
                    </div>
                  ) : (
                    <Text type="secondary" style={{ fontSize: 11 }}>No preview</Text>
                  )
                ),
              },
              {
                title: 'Title',
                dataIndex: 'name',
                key: 'name',
                render: (text, record) => (
                  <Button
                    type="link"
                    onClick={() => handleOpenProject(record.id)}
                    style={{ padding: 0, height: 'auto', fontWeight: 500, color: 'var(--text-primary)' }}
                  >
                    {text}
                  </Button>
                ),
              },
              {
                title: 'Sign Type',
                dataIndex: 'sign_type',
                key: 'sign_type',
                render: (text) => <Tag color="processing">{text}</Tag>,
              },
              {
                title: 'Design Speed',
                dataIndex: 'design_speed',
                key: 'design_speed',
                render: (speed) => `${speed} km/h`,
              },
              {
                title: 'Created',
                dataIndex: 'created_at',
                key: 'created_at',
                render: (date) => new Date(date).toLocaleDateString(),
              },
              {
                title: 'Actions',
                key: 'actions',
                render: (_, record) => (
                  <Space size="small" wrap>
                    <Button
                      icon={<EyeOutlined />}
                      size="small"
                      type="text"
                      onClick={(e) => handleView(e, record)}
                      title="View SVG"
                    >
                      View
                    </Button>
                    <Button
                      icon={<DownloadOutlined />}
                      size="small"
                      type="text"
                      onClick={(e) => handleDownload(e, record)}
                      title="Download SVG"
                    >
                      Download
                    </Button>
                    <Popconfirm
                      title="Delete this saved SVG?"
                      description='It will be removed from your library. This cannot be undone.'
                      okText="Delete"
                      cancelText="Cancel"
                      okButtonProps={{ danger: true, loading: deletingId === record.id }}
                      onConfirm={() => handleDeleteSvg(record)}
                    >
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        size="small"
                        type="text"
                        loading={deletingId === record.id}
                        title="Delete from library"
                      >
                        Delete
                      </Button>
                    </Popconfirm>
                  </Space>
                ),
              },
            ]}
            pagination={{ pageSize: 10 }}
            scroll={{ x: true }}
            className="svgs-table"
          />
        )}
      </div>

      {/* Preview: scrollable body + fixed footer so actions stay visible on small screens */}
      <Modal
        title={<span className="svg-preview-modal-title">{selectedProject?.name}</span>}
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        wrapClassName="svg-preview-modal-wrap"
        centered
        width="min(900px, 92vw)"
        styles={{
          container: {
            maxHeight: 'calc(100vh - 48px)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
          body: {
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            padding: '12px 20px',
          },
          footer: { flexShrink: 0, marginTop: 0 },
        }}
        destroyOnHidden
        footer={
          selectedProject ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 8,
                width: '100%',
                alignItems: 'center',
              }}
            >
              <Popconfirm
                title="Delete this saved SVG?"
                description="It will be removed from your library. This cannot be undone."
                okText="Delete"
                cancelText="Cancel"
                okButtonProps={{ danger: true, loading: deletingId === selectedProject.id }}
                onConfirm={() => handleDeleteSvg(selectedProject)}
              >
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  loading={deletingId === selectedProject.id}
                >
                  Delete
                </Button>
              </Popconfirm>
              <Space wrap style={{ marginLeft: 'auto', justifyContent: 'flex-end' }}>
                <Button onClick={() => setPreviewOpen(false)}>Close</Button>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={(e) => handleDownload(e, selectedProject)}
                >
                  Download SVG
                </Button>
                <Button
                  icon={<EditOutlined />}
                  onClick={() => {
                    setPreviewOpen(false);
                    handleOpenProject(selectedProject.id);
                  }}
                >
                  Open in Editor
                </Button>
              </Space>
            </div>
          ) : null
        }
      >
        {selectedProject && (
          <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
            {svgDataUrl && (
              <div className="svg-preview-modal-scroll-area">
                <object
                  data={svgDataUrl}
                  type="image/svg+xml"
                  className="svg-preview-modal-object"
                  aria-label="SVG Preview"
                >
                  <div
                    className="svg-preview-modal-fallback-svg"
                    dangerouslySetInnerHTML={{ __html: selectedProject.svg_content }}
                  />
                </object>
              </div>
            )}
            <div className="svg-preview-modal-meta">
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <Text type="secondary">Sign Type</Text>
                <Text strong>{selectedProject.sign_type}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <Text type="secondary">Design Speed</Text>
                <Text strong>{selectedProject.design_speed} km/h</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <Text type="secondary">Created</Text>
                <Text strong>{new Date(selectedProject.created_at).toLocaleString()}</Text>
              </div>
            </div>
          </Space>
        )}
      </Modal>
    </div>
  );
}

